import * as React from 'react';

// components
import { BootstrapTable, ButtonGroup } from 'react-bootstrap-table';
import { difference, isEmpty } from 'lodash';
import { Alert, Button } from 'react-bootstrap';
import EditorModalTable from './EditorModalTable';
import ErrorAlert from '../components/ErrorAlert';
import ModalEditor from './ModalEditor';
import Field from '../components/Field';
import Cell from './GridComponents/Cell';

// interfaces
import { IDispatcher } from '../../helpers/Dispatcher';
import { GridSchema, TableColum } from './interfaces';
import { IControlError } from '../../../core/Controls';

// libs
// helpers
import {
  Component,
  buildUrl,
  getEntries,
  getRandomKey,
  getUrlParamsEntries,
  numberToFixedString,
  parseFloatCommaDot,
  parseTagSanitizeAndWhitelist,
} from '../../helpers/Utils';

interface IGridProps {
  schema: GridSchema;
  // TODO: abstract type any
  api?: any;
  dispatcher?: IDispatcher;
  error: IControlError | undefined;
  visited: boolean;
  // TODO: abstract type any
  information: any;
  // TODO: abstract type any
  controlValue?: any;
  values: Array<{ controlid: string; value: unknown }>;
  rowIndex?: number;
  opts: any;
}

interface IGridState {
  // TODO: abstract type
  selectRow: object;
  // TODO: abstract type any
  values: Array<any>;
  showHelp: boolean;
  showNotification: boolean;
  noDataText: string;
  copiedValues: {
    [key: string]: string;
  };
  hiddenColumns: Array<TableColum>;
  optionsDropBoxUrl: Array<{ id: string; name: string }>;
}

class Grid extends Component<IGridProps, IGridState> {
  private modal: ModalEditor;

  state = {
    selectRow: {},
    values: [],
    showHelp: false,
    showNotification: true,
    noDataText: 'Нет данных для отображения',
    copiedValues: {},
    hiddenColumns: [],
    optionsDropBoxUrl: [],
  };

  componentDidMount() {
    const {
      schema: { value, extra, columns },
      controlValue,
      values,
      rowIndex,
    } = this.props;

    if (value || controlValue) {
      this.updateStateValues(value || controlValue);
    }

    if (rowIndex !== undefined && extra?.copy) {
      const { copy } = extra;
      let copiedValues = {};
      let noDataText = '';

      copy.from.forEach((value, index) => {
        const targetGrid = Array.isArray(copy.grid)
          ? copy.grid[index]
          : copy.grid;
        const controlIndex = values.findIndex(
          (value) => value.controlid === targetGrid,
        );
        const copiedValue = values[controlIndex]?.value[rowIndex]?.[value];

        if (copy.to[index]) {
          copiedValues = {
            ...copiedValues,
            [copy.to[index]]: copiedValue,
          };
        }

        if (copy.noDataText === value) {
          noDataText = copiedValue;
        }
      });

      this.setState({
        copiedValues,
        noDataText: noDataText ?? this.state.noDataText,
      });
    }

    const columnDropboxWithDataUrl = columns.find((column) => {
      return column?.data?.url;
    });

    if (columnDropboxWithDataUrl) {
      this.getOptionsDropBoxFromUrl(columnDropboxWithDataUrl, this.props.api);
    }

    this.getHiddenColumnsFromRowControl(this.props.schema);
  }

  componentDidUpdate(
    prevProps: Readonly<IGridProps>,
    prevState: Readonly<IGridState>,
  ) {
    const {
      schema: { value },
      controlValue,
    } = this.props;

    const _updatedValue = value || controlValue;
    const _prevUpdatedValue = prevProps.schema.value || prevProps.controlValue;
    const differenceNextValues = difference(_updatedValue, prevState.values)[0];
    const differencePrevValues = difference(prevState.values, _updatedValue)[0];

    if (
      _prevUpdatedValue !== _updatedValue &&
      _updatedValue !== prevState.values
    ) {
      this.updateStateValues(_updatedValue);
    }

    if (
      this.state.values !== prevState.values &&
      _updatedValue !== this.state.values
    ) {
      // После того как добавилась новая строка, определяем ее, и обновляем значения других полей
      if (differenceNextValues || differencePrevValues) {
        const isActionValues: 'add' | 'delete' | 'update' =
          // eslint-disable-next-line no-nested-ternary
          _updatedValue.length > prevState.values.length
            ? 'add'
            : _updatedValue.length === prevState.values.length
            ? 'update'
            : 'delete';
        this.actionAfterSomeActionWithTable(
          differenceNextValues,
          isActionValues,
          differencePrevValues,
        );
      }
    } else if (
      _updatedValue !== this.state.values &&
      this.state.values === prevState.values
    ) {
      if (
        (differenceNextValues || differencePrevValues) &&
        _updatedValue?.length !== prevState.values?.length
      ) {
        this.actionAfterSomeActionWithTable(
          differenceNextValues,
          'delete',
          differencePrevValues,
        );
      }
    }
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  onClick = () => {
    if (!isEmpty(this.state.selectRow)) {
      this.modal.onShow('rows');
    }
  };

  getFieldKey = () => {
    const { columns } = this.props.schema;
    const idx = columns?.findIndex((col: TableColum) => col.isKey);
    return columns[idx].field;
  };

  applyTableLogic = (values: Array<any>) => {
    const { schema } = this.props;

    if (schema?.extra?.logic?.type === 'column_sum') {
      for (const v of values) {
        if (v.spec_summary) {
          /*
           * строка с "Итого" должна быть в конце массива
           * она всё равно вычислимая, удалим и
           * добавим в конец заново
           */
          const index = values.indexOf(v);
          if (index > -1) {
            values.splice(index, 1);
          }
          break;
        }
      }
      if (values.length === 0) {
        // множество пусто, строка "Итого" не требуется
        return;
      }
      const summary_row = {
        spec_summary: true,
      };
      for (const col of schema.columns) {
        if (col.isKey) {
          summary_row[col.field] = `key_${getRandomKey()}`;
        } else {
          summary_row[col.field] = 0;
        }
      }
      values.push(summary_row);
      for (const v of values) {
        if (v.spec_summary) continue;
        for (const col of schema.columns) {
          const cur_v = parseFloatCommaDot(v[col.field]);
          if (!Number.isNaN(cur_v)) {
            summary_row[col.field] += cur_v;
          }
        }
      }
      for (const col of schema.columns) {
        if (schema.extra.logic?.override_columns[col.field]) {
          summary_row[col.field] =
            schema.extra.logic.override_columns[col.field];
        } else {
          summary_row[col.field] = numberToFixedString(summary_row[col.field]);
        }
      }
    }
  };

  onSave = (row: object, mode: string) => {
    if (row === undefined) return;
    const { dispatcher, schema } = this.props;
    const fieldKey = this.getFieldKey();
    const values = Array.isArray(this.state.values)
      ? [...this.state.values]
      : [];
    const index =
      Array.isArray(values) &&
      values?.findIndex((val: object) => val[fieldKey] === row[fieldKey]);

    if (mode !== 'title') {
      if (index > -1) {
        values[index] = row;
      } else {
        values.push(row);
      }
      this.applyTableLogic(values);
      this.reassignRowNumbers(values);
    } else {
      const titleRow = this.getTitleRow();
      if (titleRow) {
        if (index > -1) {
          values[index] = row;
        } else {
          values.push(row);
        }
      } else {
        values.push(row);
      }
    }
    this.setState({ values: [...values] });
    if (this.state?.selectRow[fieldKey] === row[fieldKey]) {
      this.setState({ selectRow: row });
    }

    dispatcher.onBlur(schema.id, values, true, undefined);
  };

  onDelete = () => {
    const { schema, dispatcher, controlValue } = this.props;

    if (schema?.extra?.fakeDeleteEnabled) {
      const fieldKey = this.getFieldKey();
      const values = (
        typeof schema.value === 'string'
          ? JSON.parse(schema?.value || controlValue)
          : schema?.value || controlValue
      )?.map((value: any) => {
        if (value[fieldKey] === this.state.selectRow[fieldKey]) {
          value.isDeleted = true;
        }
        return value;
      });

      dispatcher.onBlur(schema.id, values, true, undefined);

      const filteredValues = values.filter((value: any) => !value.isDeleted);
      this.applyTableLogic(filteredValues);
      this.reassignRowNumbers(filteredValues);
      this.setState({
        values: [...filteredValues],
        selectRow: {},
      });
    } else if (!isEmpty(this.state.selectRow)) {
      const fieldKey = this.getFieldKey();
      const values = (
        typeof schema.value === 'string'
          ? JSON.parse(schema?.value || controlValue)
          : schema?.value || controlValue
      )?.filter((o: object) => o[fieldKey] !== this.state.selectRow[fieldKey]);

      this.applyTableLogic(values);
      this.reassignRowNumbers(values);
      dispatcher.onBlur(schema.id, values, true, undefined);
      this.setState({
        values: [...values],
        selectRow: {},
      });
    }
  };

  reassignRowNumbers = (values) => {
    const { schema } = this.props;
    const num_columns: TableColum[] = [];

    for (const c of schema.columns) {
      if (c.name === '№') {
        num_columns.push(c);
      }
    }

    if (num_columns.length === 0) return;
    for (let idx = 0; idx < values?.length; idx++) {
      for (const nc of num_columns) {
        values[idx][nc.field] = (idx + 1).toString();
      }
    }
  };

  actionAfterSomeActionWithTable = (
    differenceNextValues: any,
    action: 'add' | 'delete' | 'update',
    differencePrevValues: any,
  ) => {
    const { schema, dispatcher } = this.props;
    const values = Array.isArray(this.state.values)
      ? [...this.state.values]
      : [];

    // Добавляем только те значения которые нужны в поле
    for (const column of schema.columns) {
      if (column?.extra?.duplicateOrSumValueForId) {
        const sum = +(differenceNextValues?.[column.field] ?? 0);
        const valueFromId = +(
          dispatcher.pullValue(column.extra.duplicateOrSumValueForId) ?? 0
        );
        let resultAction;

        if (action === 'add') {
          resultAction = valueFromId + sum;
        } else if (action === 'delete') {
          resultAction =
            valueFromId - +(differencePrevValues?.[column.field] ?? 0);
        } else if (action === 'update') {
          resultAction =
            valueFromId - +(differencePrevValues?.[column.field] ?? 0) + sum;
        }

        dispatcher.onBlur(
          column.extra.duplicateOrSumValueForId,
          resultAction,
          true,
          undefined,
        );
      }
    }

    // Добавляем все значения в таблицу
    if (schema?.extra?.duplicateValueInGridId && action === 'add') {
      const pulledValues = dispatcher.pullValue(
        schema.extra.duplicateValueInGridId,
      );
      const mergedValues = values.map((item, index) => {
        const havePulledValue = pulledValues?.[index];
        return havePulledValue ? pulledValues[index] : item;
      });

      dispatcher.onBlur(
        schema.extra.duplicateValueInGridId,
        mergedValues,
        true,
        undefined,
      );
    }
  };

  updateStateValues = (value) => {
    const _values = typeof value === 'string' ? JSON.parse(value) : value;
    this.reassignRowNumbers(_values);
    this.setState({
      values: _values?.filter((value: any) => !value.isDeleted),
    });
  };

  onDbClick = (row) => {
    this.setState({ selectRow: row });
    this.modal.onShow('rows');
  };

  onRowDelete = (row) => {
    this.setState({ selectRow: row });
    setTimeout(() => this.onDelete(), 500);
  };

  helpClick = () => {
    this.setState(({ showHelp }) => ({ showHelp: !showHelp }));
  };

  notificationClick = () => {
    this.setState(({ showNotification }) => ({
      showNotification: !showNotification,
    }));
  };

  getDataRows = () =>
    (Array.isArray(this.state?.values) &&
      this.state?.values?.filter((row) => !row.spec_col_names)) ||
    [];

  getLastColumnForSum = (values) => {
    if (
      !values ||
      (Array.isArray(values) && values?.find((item) => item?.key === 'sumCol'))
    ) {
      return null;
    }

    const result = {};
    if (this.props.schema?.extra?.funcRow) {
      // eslint-disable-next-line guard-for-in
      for (const index in this.props.schema.extra.funcRow) {
        const item: { target: string; action: string } =
          this.props.schema.extra.funcRow[index];

        if (item.action === 'sumCol') {
          result[item.target] = values.reduce(
            (acc, itemState) => acc + +itemState[item.target],
            0,
          );
        }
      }
    }

    // Заполним недостающие поля в последней строке (если она не нулл)
    if (Object.keys(result).length > 0) {
      // Определим обьект с полями которых нет
      const needFields = [];
      for (const value in values[values.length - 1]) {
        if (!result?.[value]) {
          needFields.push(value);
        }
      }

      // Запишем недостающие поля
      for (const needField of needFields) {
        result[needField] = needField === 'key' ? 'sumCol' : '';
      }
    }

    return Object.keys(result).length > 0 ? result : null;
  };

  getTitleRow = () => {
    const _row =
      (Array.isArray(this.state?.values) &&
        this.state?.values?.filter((row) => row.spec_col_names)) ||
      [];
    return _row.length > 0 ? _row[0] : null;
  };

  getControlFromRowSection = (rowSection, schemaId) => {
    return rowSection.controls.find((control) => {
      if (control.type === 'Grid') {
        return control.id === schemaId;
      }
      if (control.type === 'Group') {
        return control.columns.find((column) => {
          return column.id === schemaId;
        });
      }
    });
  };

  getHiddenColumnsFromRowControl = (gridControl) => {
    const {
      opts: { rowSection },
      schema: { id },
    } = this.props;
    let rowControl;
    if (rowSection) {
      rowControl = this.getControlFromRowSection(rowSection, id);
    }

    let hiddenColumns = [];
    if (rowControl && rowControl.type === 'Group') {
      const result = rowControl.columns.find((column) => {
        return column.id === gridControl.id;
      });

      hiddenColumns = result.columns.reduce((acc, column, index) => {
        const isHidden = !gridControl.columns.some((elem) => {
          return column.field === elem.field;
        });

        if (isHidden) {
          return {
            ...acc,
            [index]: column,
          };
        }

        return acc;
      }, {});
    } else {
      hiddenColumns = rowControl?.columns.reduce((acc, column, index) => {
        const isHidden = !gridControl.columns.some((elem) => {
          return column.field === elem.field;
        });

        if (isHidden) {
          return {
            ...acc,
            [index]: column,
          };
        }

        return acc;
      }, {});
    }
    // this.setState({ hiddenColumns: [...hiddenColumns] });
    return hiddenColumns;
  };

  concatColumnWithSaveIndex = (
    array: any[],
    concatHashObject: { [key: string]: any },
  ) => {
    const result = [...array];
    for (let item in concatHashObject) {
      result.splice(+item, 0, concatHashObject[item]);
    }
    return result;
  };

  getOptionsDropBoxFromUrl = (column, api) => {
    const columnDropboxWithUrlParams = this.props.schema.columns.find(
      (column) => {
        return column?.data?.urlParams && column.urlParams;
      },
    );

    const urlParams = columnDropboxWithUrlParams
      ? getUrlParamsEntries(
          columnDropboxWithUrlParams?.data?.urlParams,
          columnDropboxWithUrlParams.urlParams,
        )
      : null;

    const dataUrlSubstituted = buildUrl(column.data?.url, api.options);
    if (!api.options.baseUrl) {
      new Error('Исправьте конфигурацию, не добавлен baseUrl');
    }

    api?.moduleApi?.get(
      `${api.options.baseUrl}${dataUrlSubstituted}${urlParams || ''}`,
      columnDropboxWithUrlParams?.data?.query || null,
      {
        success: (response) => {
          this.setState({ optionsDropBoxUrl: [...response.data] });
        },
        error: (error) => {
          console.log(error);
        },
      },
    );
  };

  render() {
    const {
      schema: {
        disabled,
        disableViewAndEditButton,
        maxRowCount,
        visible,
        extra,
        ifFieldIsFilledThenBlockRow,
      },
      error,
      api,
      dispatcher,
    } = this.props;

    const buttonColumn = {
      isKey: false,
      field: 'actionField',
      type: 'action',
      description: 'Column for buttons',
    };
    const getColumns = () => [
      ...this.props.schema.columns.filter((column) => !column.hideColumn),
      buttonColumn,
    ];

    const hiddenColumns = this.getHiddenColumnsFromRowControl(
      this.props.schema,
    );
    const fullColumnWithoutHidden = this.concatColumnWithSaveIndex(
      this.props.schema.columns,
      hiddenColumns,
    );

    const { noDataText } = this.state;
    const length = this.state.values ? this.getDataRows().length : 0;
    const titleRow = this.getTitleRow();
    const errorMessage: any = error && error.message;
    const optionsFromUrl = this.state.optionsDropBoxUrl;

    const containerClass = 'react-bs-container-body_v1';

    const config = {
      insertRow:
        !disabled &&
        (maxRowCount ? length < maxRowCount : true) &&
        (extra?.add_row_enabled !== undefined ? extra.add_row_enabled : true),
      deleteRow:
        !disabled &&
        (extra?.del_row_enabled !== undefined ? extra.add_row_enabled : true),
      options: {
        insertText: 'Добавить',
        noDataText,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onRowDoubleClick: !disabled ? this.onDbClick : () => {},
        deleteBtn: () => (
          // @ts-ignore
          <Button onClick={this.onDelete} bsStyle="danger">
            <span className="fa fa-trash" />
            Удалить
          </Button>
        ),
        btnGroup: (props) => (
          <ButtonGroup>
            {props.insertBtn}
            {this.props?.schema?.notification && ( // @ts-ignore
              <Button
                onClick={this.notificationClick}
                bsStyle="primary"
                title="Справка"
              >
                <span className="glyphicon glyphicon-info-sign" />
              </Button>
            )}
            {this.props?.schema?.description && ( // @ts-ignore
              <Button
                onClick={this.helpClick}
                bsStyle="primary"
                title="Справка"
              >
                <span className="glyphicon glyphicon-question-sign" />
              </Button>
            )}
            {this.props?.schema?.extra?.refresh_value
              ? (() => {
                  const {
                    button_title,
                    button_type,
                    required_role,
                    required_status,
                  } = this.props.schema.extra.refresh_value;
                  const { userRole, formStatus } = this.props.information;
                  const colors = {
                    add: 'info',
                    edit: 'success',
                    delete: 'danger',
                  };

                  if (
                    required_role.includes(userRole) &&
                    required_status.includes(formStatus)
                  ) {
                    return (
                      // @ts-ignore
                      <Button
                        onClick={() =>
                          this.props.dispatcher.gridRefreshValue(
                            this.props.schema.id,
                          )
                        }
                        bsStyle={colors[button_type]}
                        title={button_title}
                      >
                        {button_title}
                      </Button>
                    );
                  }
                  return null;
                })()
              : null}
          </ButtonGroup>
        ),
        insertModal: (onModalClose) => (
          <EditorModalTable
            title="Добавить"
            onModalClose={onModalClose}
            columns={fullColumnWithoutHidden}
            hiddenColumns={hiddenColumns}
            titleRow={titleRow}
            rowCount={length}
            onSave={this.onSave}
            api={api}
            opts={this.props.opts}
            mode="rows"
            disabled={disabled}
            dispatcher={dispatcher}
            schema={this.props.schema}
            copiedValues={this.state.copiedValues}
          />
        ),
      },
    };

    return visible === undefined || visible ? (
      <Field
        {...this.props.schema}
        status={isEmpty(this.getDataRows()) ? 'default' : 'success'}
        fullwidth={extra?.design?.includes('fullwidth')}
      >
        {extra?.hint && (
          <div className="alert alert-info hint">
            {parseTagSanitizeAndWhitelist(extra.hint, extra?.allowed_tags)}
          </div>
        )}
        <BootstrapTable
          {...config}
          data={this.getDataRows().map((row) => {
            //Если есть поле которое блокирует всю строку, то блокируем ее, иначе стандартное поведение
            let gridRowDisabled = ifFieldIsFilledThenBlockRow
              ? !!row?.[ifFieldIsFilledThenBlockRow] ?? disabled
              : disabled;
            return {
              ...row,
              actionField: true,
              extra,
              disabled: gridRowDisabled,
              disableViewAndEditButton,
              onDbClick: this.onDbClick,
              onRowDelete: this.onRowDelete,
            };
          })}
          bodyContainerClass={containerClass}
          trClassName={(row, rowIdx) => {
            let searchThisRow;
            if (
              errorMessage &&
              typeof errorMessage === 'object' &&
              errorMessage.length > 0
            ) {
              searchThisRow = errorMessage.find((item) => {
                if (typeof item.index === 'number' && item.index >= 0) {
                  return item.index === rowIdx;
                }
                return null;
              });
            }
            return searchThisRow ? 'alert-danger' : '';
          }}
        >
          {getColumns().map((column: TableColum, idx: number) =>
            Cell({
              column,
              idx,
              titleRow,
              optionsFromUrl,
            }),
          )}
        </BootstrapTable>

        {this.getLastColumnForSum(this.state.values) ? (
          <BootstrapTable
            data={[this.getLastColumnForSum(this.state.values)]}
            bodyContainerClass={containerClass}
          >
            {getColumns().map((column: TableColum, idx: number) =>
              Cell({
                column,
                idx,
                titleRow,
                last: true,
              }),
            )}
          </BootstrapTable>
        ) : null}

        <ModalEditor
          ref={(modal) => {
            this.modal = modal;
          }}
          title="Редактировать"
          columns={fullColumnWithoutHidden}
          hiddenColumns={hiddenColumns}
          rowCount={length}
          onSave={this.onSave}
          titleRow={titleRow}
          cells={this.state.selectRow}
          disabled={disabled}
          data={this.getDataRows()}
          opts={this.props.opts}
          api={api}
          dispatcher={dispatcher}
          schema={this.props.schema}
        />

        <ErrorAlert errorMessage={errorMessage} />

        {this.state.showNotification &&
          this.props.schema?.notification && ( // @ts-ignore
            <Alert bsStyle="warning">
              {parseTagSanitizeAndWhitelist(
                this.props.schema.notification?.ru,
                this.props.schema?.extra?.allowed_tags,
              )}
            </Alert>
          )}

        {this.state.showHelp &&
          this.props.schema?.description && ( // @ts-ignore
            <Alert bsStyle="warning">
              {parseTagSanitizeAndWhitelist(
                this.props.schema.description?.ru,
                this.props.schema?.extra?.allowed_tags,
              )}
            </Alert>
          )}
      </Field>
    ) : (
      <div />
    );
  }
}

export default Grid;
